import React, { useState } from 'react';

export default function BulkUpload({ uploading, uploadResult, handleBulkUpload }) {
  const [selectedMediaTypes, setSelectedMediaTypes] = useState({});

  const handleMediaTypeChange = (field, value) => {
    setSelectedMediaTypes(prev => ({ ...prev, [field]: value }));
  };

  const downloadTemplate = (type) => {
    let headers = [];
    let filename = '';
    
    switch(type) {
      case 'users':
        headers = ['Email', 'Name', 'Role', 'Password'];
        filename = 'users_template.csv';
        break;
      case 'clients':
        headers = ['Company Name'];
        filename = 'clients_template.csv';
        break;
      case 'media-resources':
        headers = ['Publication Name'];
        filename = 'publications_template.csv';
        break;
      case 'editions':
        headers = ['Edition Name'];
        filename = 'editions_template.csv';
        break;
      case 'positions':
        headers = ['Position Name'];
        filename = 'positions_template.csv';
        break;
      default:
        return;
    }

    const csvContent = "data:text/csv;charset=utf-8," + headers.join(",");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div className="admin-header" style={{ marginBottom: '2rem' }}>
        <div>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Bulk Data Management</h3>
          <p>Upload Excel files to populate master data across the system</p>
        </div>
      </div>
      
      <div className="bulk-grid">
        {[
          { label: 'Users', field: 'users', desc: 'Email, Name, Role, Password' },
          { label: 'Clients', field: 'clients', desc: 'Company Name' },
          { label: 'Publications', field: 'media-resources', desc: 'Publication Name', needsMediaType: true },
          { label: 'Editions', field: 'editions', desc: 'Edition Name' },
          { label: 'Positions', field: 'positions', desc: 'Position Name' },
        ].map((item) => (
          <div className="bulk-upload-card" key={item.field}>
            <div className="bulk-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              <h4 style={{ margin: 0 }}>{item.label}</h4>
              <button 
                onClick={() => downloadTemplate(item.field)}
                style={{
                  background: '#f0f4ff',
                  color: '#1c4fe0',
                  border: '1px solid #1c4fe0',
                  borderRadius: '0.4rem',
                  padding: '0.2rem 0.5rem',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}
              >
                <span>📥</span> Template
              </button>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 1rem 0' }}>
              Required column: <strong>{item.desc}</strong>
            </p>
            
            {item.needsMediaType && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '0.4rem' }}>
                  Select Media Type for all rows:
                </label>
                <select 
                  value={selectedMediaTypes[item.field] || ''} 
                  onChange={(e) => handleMediaTypeChange(item.field, e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.5rem', 
                    borderRadius: '0.4rem', 
                    border: '1px solid #e2e8f0',
                    fontSize: '0.85rem'
                  }}
                >
                  <option value="">-- Select Type --</option>
                  <option value="PRINT">Print</option>
                  <option value="RADIO">Radio</option>
                  <option value="TV">TV</option>
                  <option value="DIGITAL">Digital</option>
                </select>
              </div>
            )}

            <div className="file-input-wrapper">
              <input type="file" accept=".csv,.xls,.xlsx" id={`upload-${item.field}`} />
              <div className="file-input-label">
                <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</span>
                <span>Choose Excel File</span>
              </div>
            </div>
            <button
              className="upload-btn"
              type="button"
              disabled={uploading || (item.needsMediaType && !selectedMediaTypes[item.field])}
              onClick={() => {
                const input = document.getElementById(`upload-${item.field}`);
                if (input?.files?.length) {
                  const extraParams = {};
                  if (item.needsMediaType) {
                    extraParams.mediaType = selectedMediaTypes[item.field];
                  }
                  handleBulkUpload(item.field, input.files[0], extraParams);
                }
              }}
              style={{
                opacity: (uploading || (item.needsMediaType && !selectedMediaTypes[item.field])) ? 0.5 : 1,
                cursor: (uploading || (item.needsMediaType && !selectedMediaTypes[item.field])) ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading ? 'Uploading...' : 'Start Upload'}
            </button>
          </div>
        ))}
      </div>

      {uploadResult && (
        <div className="card" style={{ marginTop: '2rem', border: '1px solid #e2e8f0', background: '#f8fafc', padding: '1.5rem' }}>
          <div style={{ fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
            <span>📈</span> {uploadResult.type?.toUpperCase()} Upload Results
          </div>
          {uploadResult.error ? (
            <div style={{ color: '#ef4444', padding: '1rem', background: '#fef2f2', borderRadius: '0.5rem', border: '1px solid #fee2e2' }}>
              {uploadResult.error}
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto', background: '#fff', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {uploadResult.results.map((row, idx) => (
                  <li key={idx} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between' }}>
                    <span>
                      <span style={{ fontWeight: 700, color: '#64748b', marginRight: '1rem' }}>Row {row.row}:</span>
                      <span style={{ color: '#334155' }}>{row.message || 'Processed'}</span>
                    </span>
                    <span className={`status-badge ${row.status === 'success' ? 'active' : 'inactive'}`} style={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      {row.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

